import { useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { RegisterEntry } from "@/api/types";
import "./RegisterCheckControls.css";
import { useRegisterCheckNotice } from "./useRegisterCheckNotice";

export interface RegisterCheckControlsProps {
  entry: RegisterEntry;
  /** Re-reads the register's first page (the server's answer is the truth after every write). */
  refresh: () => Promise<void>;
}

type CheckState = "off" | "flagged" | "printed" | "voided";

/**
 * ef-062-6 - the per-payment check controls rendered inside every register row by `Books.tsx`'s
 * `RegisterRow`: the "Print this check" flag, the Print button, and the printed / voided marker.
 * Renders nothing for a row that cannot carry a check (a deposit, or a payment with no payee).
 *
 * The state is read off the server's answer (`entry.printToCheck` / `entry.check`); the only local
 * state is the optimistic mirror of the box (same idiom as the row's Cleared checkbox) and the in-flight
 * flag; the last error lives in `useRegisterCheckNotice` so it survives the register's re-read.
 */
export function RegisterCheckControls({ entry, refresh }: RegisterCheckControlsProps) {
  const [flagged, setFlagged] = useState(entry.printToCheck);
  const [pending, setPending] = useState(false);
  // Outside the component: `refresh()` remounts every row, and a 409 alert must outlive that.
  const [error, setError] = useRegisterCheckNotice(entry.entryLineId);

  useEffect(() => {
    setFlagged(entry.printToCheck);
  }, [entry.printToCheck]);

  // Only a payment with a payee can carry a check.
  if (!(entry.amount < 0 && entry.vendorName !== null)) return null;

  const check = entry.check;
  const state: CheckState = check ? (check.voided ? "voided" : "printed") : flagged ? "flagged" : "off";
  const boxChecked = state !== "off";

  async function handleFlagChange(next: boolean): Promise<void> {
    const previous = flagged;
    setFlagged(next);
    setPending(true);
    setError(null);
    try {
      await demoApi.setPrintToCheck(entry.entryLineId, next);
      await refresh();
    } catch (err) {
      setFlagged(previous);
      setError(problemMessage(err, "Could not update this check."));
    } finally {
      setPending(false);
    }
  }

  async function handlePrint(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await demoApi.printRegisterCheck(entry.entryLineId);
      await refresh();
    } catch (err) {
      setError(problemMessage(err, "Could not print this check."));
      // A 409 means another window printed it first - re-read so the row shows that number.
      try {
        await refresh();
      } catch {
        /* the alert above already tells the farmer; the next refresh will catch up */
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div role="group" aria-label="Check printing" className="register-check" data-state={state}>
      <label className="register-check__flag">
        <input
          type="checkbox"
          checked={boxChecked}
          disabled={pending || state === "printed"}
          onChange={(e) => void handleFlagChange(e.target.checked)}
        />
        <span>Print this check</span>
      </label>
      {state === "flagged" && (
        <button type="button" className="btn btn--ghost register-check__action" disabled={pending} onClick={() => void handlePrint()}>
          Print check
        </button>
      )}
      {state === "printed" && check && (
        <>
          <span className="register-check__number">Check #{check.checkNumber}</span>
          <a className="register-check__link" href={`/books/checks/${entry.entryLineId}`}>
            View check #{check.checkNumber}
          </a>
        </>
      )}
      {state === "voided" && check && (
        <>
          <span className="register-check__number">Check #{check.checkNumber} voided</span>
          <button type="button" className="btn btn--ghost register-check__action" disabled={pending} onClick={() => void handlePrint()}>
            Print again
          </button>
        </>
      )}
      {error && (
        <span className="register-check__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
